import React, { useState } from 'react';
import {
    Plus, ChevronRight, Check, MessageCircle, Calendar,
    Clock, Globe, Building2, Users, ArrowRight,
    GraduationCap, Utensils, Heart, Activity, Scissors,
    Car, Camera, Home, Dog, Plane, ShoppingBag, Briefcase,
    Smile, Share2, Sparkles, Phone, Trash2, Edit3, Mail
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';

const OnboardingPage = () => {
    const { t } = useLanguage();
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        industry: '',
        specialty: '',
        companySize: '',
        companyName: '',
        phoneNumber: '',
        automatedReminders: {
            confirmation: true,
            rem24h: true,
            rem2h: true
        },
        onlinePresence: null,
        automateMessaging: true
    });

    const navigate = useNavigate();

    const industries = [
        { id: 'salud', name: t('onboarding.ind.salud'), desc: t('onboarding.ind.saludDesc'), icon: <Plus className="text-emerald-500" /> },
        { id: 'bienestar', name: t('onboarding.ind.bienestar'), desc: t('onboarding.ind.bienestarDesc'), icon: <Heart className="text-cyan-400" /> },
        { id: 'belleza', name: t('onboarding.ind.belleza'), desc: t('onboarding.ind.bellezaDesc'), icon: <Scissors className="text-pink-400" /> },
        { id: 'fitness', name: t('onboarding.ind.fitness'), desc: t('onboarding.ind.fitnessDesc'), icon: <Activity className="text-blue-500" /> },
        { id: 'auto', name: t('onboarding.ind.auto'), desc: t('onboarding.ind.autoDesc'), icon: <Car className="text-gray-500" /> },
        { id: 'educacion', name: t('onboarding.ind.educacion'), desc: t('onboarding.ind.educacionDesc'), icon: <GraduationCap className="text-indigo-500" /> },
        { id: 'creativos', name: t('onboarding.ind.creativos'), desc: t('onboarding.ind.creativosDesc'), icon: <Camera className="text-purple-500" /> },
        { id: 'alimentos', name: t('onboarding.ind.alimentos'), desc: t('onboarding.ind.alimentosDesc'), icon: <Utensils className="text-orange-500" /> },
        { id: 'hogar', name: t('onboarding.ind.hogar'), desc: t('onboarding.ind.hogarDesc'), icon: <Home className="text-yellow-600" /> },
        { id: 'mascotas', name: t('onboarding.ind.mascotas'), desc: t('onboarding.ind.mascotasDesc'), icon: <Dog className="text-orange-400" /> },
        { id: 'inmobiliaria', name: t('onboarding.ind.inmobiliaria'), desc: t('onboarding.ind.inmobiliariaDesc'), icon: <Building2 className="text-blue-600" /> },
        { id: 'viajes', name: t('onboarding.ind.viajes'), desc: t('onboarding.ind.viajesDesc'), icon: <Plane className="text-sky-500" /> },
        { id: 'retail', name: t('onboarding.ind.retail'), desc: t('onboarding.ind.retailDesc'), icon: <ShoppingBag className="text-red-500" /> },
        { id: 'pro', name: t('onboarding.ind.pro'), desc: t('onboarding.ind.proDesc'), icon: <Briefcase className="text-gray-700" /> },
    ];

    const saludSpecialties = [
        t('onboarding.spec.aestheticMedicine'), t('onboarding.spec.cardiology'), t('onboarding.spec.dentistry'), t('onboarding.spec.dermatology'),
        t('onboarding.spec.endocrinology'), t('onboarding.spec.ent'), t('onboarding.spec.multiClinic'),
        t('onboarding.spec.gynecology'), t('onboarding.spec.neurology'), t('onboarding.spec.oncology'), t('onboarding.spec.ophthalmology'), t('onboarding.spec.orthopedics'),
        t('onboarding.spec.pediatrics'), t('onboarding.spec.mentalHealth'), t('onboarding.spec.surgery'), t('onboarding.spec.other')
    ];

    const nextStep = () => setStep(prev => prev + 1);
    const prevStep = () => setStep(prev => prev - 1);

    const StepIndicator = ({ number, title, active }) => (
        <div className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${active ? 'bg-white/10 text-white' : 'text-slate-400'}`}>
            <div className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold ${active ? 'bg-blue-500 text-white' : 'bg-slate-700 text-slate-400'}`}>
                {number}
            </div>
            <span className="text-sm font-medium">{title}</span>
        </div>
    );

    return (
        <div className="flex min-h-screen bg-slate-50">
            {/* Sidebar */}
            <div className="w-80 bg-[#0f172a] p-8 flex flex-col hidden lg:flex">
                <div className="mb-12">
                    <img
                        src="https://customer-assets.emergentagent.com/job_convocreator/artifacts/ob9zhg81_logo%20transparente.png"
                        alt="ChatAlfa"
                        className="h-16 brightness-0 invert"
                    />
                </div>

                <div className="flex-1 space-y-2">
                    <h2 className="text-white font-bold mb-6">{t('onboarding.companyInfo')}</h2>
                    <StepIndicator number={1} title={t('onboarding.step1Title')} active={step <= 2} />
                    <StepIndicator number={2} title={t('onboarding.step2Title')} active={step >= 3 && step <= 4} />
                    <StepIndicator number={3} title={t('onboarding.step3Title')} active={step === 5} />
                    <StepIndicator number={4} title={t('onboarding.step4Title')} active={step >= 6} />
                </div>

                <div className="mt-auto pt-8 border-t border-white/10 text-slate-500 text-xs">
                    {t('onboarding.copyright')}
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col md:p-12 p-4 max-h-screen overflow-y-auto">
                <div className="max-w-4xl w-full mx-auto">

                    {/* Step 1: Industry Selection */}
                    {step === 1 && (
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <h1 className="text-3xl font-bold text-slate-900 mb-2">{t('onboarding.chooseIndustry')}</h1>
                            <p className="text-slate-500 mb-8">{t('onboarding.chooseIndustryDesc')}</p>

                            <div className="grid md:grid-cols-2 gap-4">
                                {industries.map((ind) => (
                                    <Card
                                        key={ind.id}
                                        className={`cursor-pointer hover:border-blue-500 border-2 transition-all ${formData.industry === ind.id ? 'border-blue-500 bg-blue-50/30' : 'border-transparent'}`}
                                        onClick={() => {
                                            setFormData({ ...formData, industry: ind.id });
                                            nextStep();
                                        }}
                                    >
                                        <CardContent className="p-4 flex items-center gap-4">
                                            <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center shrink-0 border border-slate-100">
                                                {ind.icon}
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-bold text-slate-900 text-sm">{ind.name}</h3>
                                                <p className="text-xs text-slate-500 line-clamp-1">{ind.desc}</p>
                                            </div>
                                            <ChevronRight className="h-4 w-4 text-slate-300" />
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Step 2: Details */}
                    {step === 2 && (
                        <div className="animate-in fade-in slide-in-from-right-4 duration-500 max-w-2xl mx-auto">
                            <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
                                <div className="bg-slate-50 h-64 flex items-center justify-center p-8">
                                    <img
                                        src="https://chatfuel.com/images/onboarding/onboarding_industry.png"
                                        alt="Intro"
                                        className="h-full object-contain rounded-xl"
                                        onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1600880212340-02d956553908?auto=format&fit=crop&q=80&w=800"; }}
                                    />
                                </div>
                                <div className="p-8">
                                    <h1 className="text-2xl font-bold text-slate-900 mb-6">{t('onboarding.chooseIndustry')}</h1>

                                    <div className="space-y-6">
                                        <div>
                                            <label className="text-xs font-bold text-slate-400 uppercase tracking-widest block mb-2">{t('onboarding.industryLabel')}</label>
                                            <select
                                                className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none appearance-none bg-slate-50"
                                                value={formData.industry}
                                                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                                            >
                                                {industries.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="text-xs font-bold text-slate-400 uppercase tracking-widest block mb-2">{t('onboarding.specialtyLabel')}</label>
                                            <select
                                                className="w-full h-12 px-4 rounded-xl border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none appearance-none bg-white"
                                                value={formData.specialty}
                                                onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                                            >
                                                <option value="">{t('onboarding.chooseSpecialty')}</option>
                                                {saludSpecialties.map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="text-xs font-bold text-slate-400 uppercase tracking-widest block mb-2">{t('onboarding.teamSizeLabel')}</label>
                                            <div className="flex gap-2">
                                                {['1', '2-5', '5-10', '10+'].map(size => (
                                                    <button
                                                        key={size}
                                                        onClick={() => setFormData({ ...formData, companySize: size })}
                                                        className={`flex-1 h-10 rounded-full text-sm font-medium transition-all ${formData.companySize === size ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                                                    >
                                                        {size}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="pt-4 flex gap-3">
                                            <Button variant="outline" onClick={prevStep} className="flex-1 h-12 rounded-xl">{t('common.back')}</Button>
                                            <Button onClick={nextStep} className="flex-1 h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white" disabled={!formData.specialty || !formData.companySize}>
                                                {t('common.continue')} <ArrowRight className="ml-2 h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Calendar */}
                    {step === 3 && (
                        <div className="animate-in fade-in slide-in-from-right-4 duration-500 max-w-2xl mx-auto">
                            <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
                                <div className="p-8">
                                    <div className="flex items-center gap-2 text-blue-600 font-bold mb-6">
                                        <Calendar className="h-5 w-5" /> {t('onboarding.digitalReceptionist')}
                                    </div>
                                    <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('onboarding.setupCalendar')}</h1>
                                    <p className="text-slate-500 mb-8">{t('onboarding.setupCalendarDesc')}</p>

                                    <div className="space-y-6">
                                        <div>
                                            <label className="text-xs font-bold text-slate-400 uppercase tracking-widest block mb-2">{t('onboarding.companyNameLabel')}</label>
                                            <Input
                                                placeholder={t('onboarding.companyNamePlaceholder')}
                                                value={formData.companyName}
                                                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                                                className="h-12 rounded-xl"
                                            />
                                        </div>

                                        <div>
                                            <label className="text-xs font-bold text-slate-400 uppercase tracking-widest block mb-2">{t('onboarding.phoneLabel')}</label>
                                            <Input
                                                placeholder={t('onboarding.phonePlaceholder')}
                                                value={formData.phoneNumber}
                                                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                                                className="h-12 rounded-xl"
                                            />
                                        </div>

                                        <div className="bg-blue-50 rounded-2xl p-6 flex items-start gap-4">
                                            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shrink-0 border border-blue-100">
                                                <Clock className="h-5 w-5 text-blue-600" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-blue-900 mb-1">{t('onboarding.saveTime')}</p>
                                                <p className="text-xs text-blue-700 leading-relaxed">{t('onboarding.saveTimeDesc')}</p>
                                            </div>
                                        </div>

                                        <div className="pt-4 flex gap-3">
                                            <Button variant="outline" onClick={prevStep} className="flex-1 h-12 rounded-xl">{t('common.back')}</Button>
                                            <Button onClick={nextStep} className="flex-1 h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white" disabled={!formData.companyName || !formData.phoneNumber}>
                                                {t('common.continue')}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Reminders */}
                    {step === 4 && (
                        <div className="animate-in fade-in slide-in-from-right-4 duration-500 max-w-2xl mx-auto">
                            <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8">
                                <div className="flex items-center gap-2 text-blue-600 font-bold mb-6">
                                    <MessageCircle className="h-5 w-5" /> {t('onboarding.autoReminders')}
                                </div>
                                <h1 className="text-2xl font-bold text-slate-900 mb-6">{t('onboarding.reduceNoShows')}</h1>

                                <div className="space-y-4 mb-8">
                                    {[
                                        { id: 'confirmation', title: t('onboarding.reminderConfirmation'), desc: t('onboarding.reminderConfirmationDesc'), key: 'confirmation' },
                                        { id: 'rem24h', title: t('onboarding.reminder24h'), desc: t('onboarding.reminder24hDesc'), key: 'rem24h' },
                                        { id: 'rem2h', title: t('onboarding.reminder2h'), desc: t('onboarding.reminder2hDesc'), key: 'rem2h' }
                                    ].map((item) => (
                                        <div key={item.id} className="flex items-center justify-between p-4 rounded-2xl border border-slate-100 hover:bg-slate-50 transition-colors">
                                            <div className="flex-1 pr-4">
                                                <h3 className="font-bold text-slate-900 text-sm">{item.title}</h3>
                                                <p className="text-xs text-slate-500">{item.desc}</p>
                                            </div>
                                            <div
                                                className={`w-12 h-6 rounded-full relative cursor-pointer transition-colors ${formData.automatedReminders[item.key] ? 'bg-blue-600' : 'bg-slate-200'}`}
                                                onClick={() => setFormData({
                                                    ...formData,
                                                    automatedReminders: {
                                                        ...formData.automatedReminders,
                                                        [item.key]: !formData.automatedReminders[item.key]
                                                    }
                                                })}
                                            >
                                                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${formData.automatedReminders[item.key] ? 'right-1' : 'left-1'}`} />
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="bg-emerald-50 rounded-2xl p-6 flex items-start gap-4 mb-8">
                                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shrink-0 border border-emerald-100">
                                        <Check className="h-5 w-5 text-emerald-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-emerald-900 mb-1">{t('onboarding.provenEfficiency')}</p>
                                        <p className="text-xs text-emerald-700 leading-relaxed">{t('onboarding.provenEfficiencyDesc')}</p>
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    <Button variant="outline" onClick={prevStep} className="flex-1 h-12 rounded-xl">{t('common.back')}</Button>
                                    <Button onClick={nextStep} className="flex-1 h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
                                        {t('common.continue')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 5: Automate Messaging */}
                    {step === 5 && (
                        <div className="animate-in fade-in slide-in-from-right-4 duration-500 max-w-2xl mx-auto">
                            <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
                                <div className="p-8">
                                    <div className="flex items-center gap-2 text-blue-600 font-bold mb-6">
                                        <Sparkles className="h-5 w-5" /> {t('onboarding.automateMessagesIntro')}
                                    </div>
                                    <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('onboarding.automateMessages')}</h1>
                                    <p className="text-slate-500 mb-8">{t('onboarding.automateMessagesDesc')}</p>

                                    <div className="space-y-4 mb-8">
                                        <div
                                            onClick={() => setFormData({ ...formData, automateMessaging: true })}
                                            className={`p-6 rounded-3xl border-2 cursor-pointer transition-all flex items-center gap-4 ${formData.automateMessaging === true ? 'border-blue-600 bg-blue-50/30' : 'border-slate-100 hover:bg-slate-50'}`}
                                        >
                                            <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center shrink-0">
                                                <Smile className="h-6 w-6 text-blue-600" />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-bold text-slate-900">{t('onboarding.yesAutomate')}</h3>
                                                <p className="text-xs text-slate-500">{t('onboarding.yesAutomateDesc')}</p>
                                            </div>
                                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${formData.automateMessaging === true ? 'border-blue-600 bg-blue-600' : 'border-slate-300'}`}>
                                                {formData.automateMessaging === true && <Check className="h-3 w-3 text-white" />}
                                            </div>
                                        </div>

                                        <div
                                            onClick={() => setFormData({ ...formData, automateMessaging: false })}
                                            className={`p-6 rounded-3xl border-2 cursor-pointer transition-all flex items-center gap-4 ${formData.automateMessaging === false ? 'border-blue-600 bg-blue-50/30' : 'border-slate-100 hover:bg-slate-50'}`}
                                        >
                                            <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center shrink-0">
                                                <Plus className="h-6 w-6 text-slate-600" rotate={45} />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-bold text-slate-900">{t('onboarding.notNow')}</h3>
                                            </div>
                                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${formData.automateMessaging === false ? 'border-blue-600 bg-blue-600' : 'border-slate-300'}`}>
                                                {formData.automateMessaging === false && <Check className="h-3 w-3 text-white" />}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex gap-3">
                                        <Button variant="outline" onClick={prevStep} className="flex-1 h-12 rounded-xl">{t('common.back')}</Button>
                                        <Button onClick={nextStep} className="flex-1 h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
                                            {t('common.continue')}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 6: First Booking (Preview) */}
                    {step === 6 && (
                        <div className="animate-in fade-in slide-in-from-right-4 duration-500 max-w-2xl mx-auto">
                            <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
                                <div className="p-8">
                                    <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('onboarding.firstBooking')}</h1>
                                    <p className="text-slate-500 mb-8">{t('onboarding.firstBookingDesc')} {formData.phoneNumber}.</p>

                                    <Card className="bg-slate-50 border-slate-200 mb-8 overflow-hidden">
                                        <div className="p-6">
                                            <div className="flex justify-between items-start mb-6">
                                                <div className="flex items-center gap-2 bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-xs font-bold">
                                                    {t('onboarding.notConfirmed')} <ChevronRight className="h-3 w-3" rotate={90} />
                                                </div>
                                                <div className="flex gap-2 text-slate-400">
                                                    <Edit3 className="h-4 w-4 cursor-pointer" />
                                                    <Trash2 className="h-4 w-4 cursor-pointer" />
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-4 mb-6">
                                                <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center text-white font-bold text-lg">
                                                    F
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-slate-900">Fernando</h3>
                                                    <div className="flex items-center gap-1 text-xs text-blue-600 font-medium">
                                                        <MessageCircle className="h-3 w-3" /> {t('common.contact')}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="space-y-4 text-sm text-slate-600">
                                                <div className="flex items-center gap-3">
                                                    <Clock className="h-4 w-4 text-slate-400" />
                                                    <span>terça-feira, 3 de fevereiro <br /> 22:22 - 23:22</span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <Phone className="h-4 w-4 text-slate-400" />
                                                    <span>{formData.phoneNumber}</span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <Sparkles className="h-4 w-4 text-slate-400" />
                                                    <span>{t('onboarding.initialConsultation')}</span>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center text-[10px] text-white">F</div>
                                                    <p>{t('onboarding.by')} {formData.companyName}</p>
                                                </div>
                                            </div>

                                            <Button className="w-full mt-6 bg-white border border-slate-200 text-blue-600 hover:bg-blue-50">
                                                <Check className="mr-2 h-4 w-4" /> {t('onboarding.markConfirmed')}
                                            </Button>
                                        </div>
                                    </Card>

                                    <div className="flex gap-3">
                                        <Button variant="outline" onClick={prevStep} className="flex-1 h-12 rounded-xl">{t('common.back')}</Button>
                                        <Button onClick={nextStep} className="flex-1 h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
                                            {t('onboarding.createFirstBooking')}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 7: Final Screen */}
                    {step === 7 && (
                        <div className="animate-in fade-in zoom-in duration-500 max-w-2xl mx-auto">
                            <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8 text-center">
                                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 text-emerald-600">
                                    <Check className="h-10 w-10" />
                                </div>
                                <h1 className="text-3xl font-bold text-slate-900 mb-2">{t('onboarding.done')}</h1>
                                <p className="text-slate-500 mb-8 max-w-md mx-auto">{t('onboarding.doneDesc')}</p>

                                <div className="mb-12">
                                    <Card className="bg-gradient-to-r from-emerald-400 to-cyan-400 p-1 rounded-2xl">
                                        <div className="bg-white p-6 rounded-xl text-left">
                                            <div className="flex items-center gap-2 bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold w-fit mb-4">
                                                {t('onboarding.confirmed')} <ChevronRight className="h-3 w-3" rotate={90} />
                                            </div>
                                            <h3 className="font-bold text-slate-900">Fernando</h3>
                                            <p className="text-sm text-slate-500">terça-feira, 3 de fevereiro • 22:22</p>
                                        </div>
                                    </Card>
                                </div>

                                <Button onClick={() => navigate('/dashboard')} className="w-full h-12 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold">
                                    {t('onboarding.finish')}
                                </Button>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default OnboardingPage;
